<script>
import axios from 'axios';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import map from 'lodash/map';
import {mapGetters} from 'vuex';
import TrekLoading from '@/components/TrekLoading';
import Tabs from '@/components/tabs/Tabs';
import CartSummary from '@/components/cart/CartSummary';
import TabPane from '@/components/tabs/TabPane';
import FormGroup from '@/components/FormGroup';
import ShippingInfo from '@/components/checkout/ShippingInfo';
import Collapse from '@/components/Collapse';
import CheckoutGrid from '@/components/checkout/CheckoutGrid';
import OrderDetails from '@/components/checkout/OrderDetails';
import Callout from '@/components/Callout';
import TrekLink from '@/components/TrekLink';

export default {
  components: {
    TrekLoading,
    Tabs,
    CartSummary,
    TabPane,
    FormGroup,
    ShippingInfo,
    Collapse,
    CheckoutGrid,
    OrderDetails,
    Callout,
    TrekLink,
  },

  props: {
    checkoutStep: {
      type: String,
      default: '',
      required: false,
    },
    orderCode: {
      type: String,
      default: '',
      required: false,
    },
    skuNotAvailableText: {
      type: String,
      default: '',
      required: false,
    },
    unavailableItems: {
      type: Array,
      default: () => [],
      required: false,
    },
    orderConfirmationCallout: {
      type: Array,
      default: () => [],
      required: false,
    },
    bikesOrderCode: {
      type: String,
      default: '',
      required: false,
    },
    partsOrderCode: {
      type: String,
      default: '',
      required: false,
    },
  },

  data() {
    return {
      cartData: {
        bikes: null,
        parts: null,
        cartSummary: null,
      },
      calculatedFreight: {
        bikes: false,
        parts: false,
      },
      loading: true,
      types: ['bikes', 'parts'],
      selectedTabName: '',
      showSaveWarning: false,
      showCartValidationWarning: false,
      portOfDeliveryMaxChars: 11,
      asyncOrderInterval: '',
      thankYouMessage: '',
      timeoutError: false,
      emptyReturnError: false,
      orderData: '',
      orderNumber: '',
      bikesOrderUpdate: this.bikesOrderCode ? this.bikesOrderCode : '',
      partsOrderUpdate: this.partsOrderCode ? this.partsOrderCode : '',
    };
  },

  computed: {
    bikes() {
      return this.cartData.bikes;
    },

    parts() {
      return this.cartData.parts;
    },

    portOfDeliveryCharsLeftBikes() {
      if (!this.cartData.bikes.orderDetails.portofDelivery) return this.portOfDeliveryMaxChars.toString();
      else {
        let charsLeft = this.portOfDeliveryMaxChars - this.cartData.bikes.orderDetails.portofDelivery.length;
        if (charsLeft < 0) return '0';
        else return charsLeft.toString();
      }
    },

    portOfDeliveryCharsLeftParts() {
      if (!this.cartData.parts || !this.cartData.parts.orderDetails.portofDelivery)
        return this.portOfDeliveryMaxChars.toString();
      else {
        let charsLeft = this.portOfDeliveryMaxChars - this.cartData.parts.orderDetails.portofDelivery.length;
        if (charsLeft < 0) return '0';
        else return charsLeft.toString();
      }
    },

    showReviewButton() {
      return this.cartData.bikes && this.cartData.parts;
    },

    tabSelected: {
      get() {
        return this.selectedTabName || (isEmpty(this.bikes) ? 'parts' : 'bikes');
      },
      set(name) {
        this.selectedTabName = name;
        if (this.checkoutStep === 'confirmation') {
          this.setMessages(false);
        }
      },
    },

    bikesOrderDetails() {
      if (this.cartData.bikes && this.cartData.bikes.orderDetails) {
        return get(this, 'cartData.bikes.orderDetails', {});
      }

      return {};
    },

    partsOrderDetails() {
      if (this.cartData.parts && this.cartData.parts.orderDetails) {
        return get(this, 'cartData.parts.orderDetails', {});
      }

      return {};
    },

    endpoint() {
      switch (this.checkoutStep) {
        case 'shipments':
          return '/checkout/multi/checkout/shipmentsInfo?groupBy=warehouse&includes=parts,bikes';
        case 'orderReview':
          return '/checkout-b2b/orderInfo';
        case 'confirmation':
          return `/checkout/multi/order-review/orderSummaryInfo?orderCode=${this.orderCode}`;
        default:
          console.error('No checkout category provided');
          return undefined;
      }
    },

    summaryTotalItems() {
      const bikesTotal = parseInt(get(this.cartData, 'cartSummary.bikes.totalItems', 0)) || 0;
      const partsTotal = parseInt(get(this.cartData, 'cartSummary.parts.totalItems', 0)) || 0;
      return bikesTotal + partsTotal;
    },

    ...mapGetters('advancedOrdering', ['advancedOrderingMode', 'aoToggleCompletedWatcher']),
    ...mapGetters('backend', ['consumerFriendlyMode']),
  },

  watch: {
    aoToggleCompletedWatcher() {
      this.loadCart();
    },
  },

  mounted() {
    this.displayUnavailableItemsWarnings();
    this.loadCart();
    if (this.checkoutStep === 'confirmation' && !this.orderData) {
      this.asyncOrderCheck();
    }
  },

  methods: {
    cartSummaryLoaded() {
      if (this.checkoutStep === 'shipments') {
        this.enableShipmentsSubmitListeners();
      }
    },

    validateCartData() {
      if (this.checkoutStep === 'orderReview') {
        this.performCartValidation();
      }
    },

    setMessages(fromApi) {
      if (fromApi) {
        if (this.bikesOrderUpdate) {
          this.orderNumber = this.bikesOrderUpdate;
          this.createThankYouMessage(this.bikesOrderUpdate);
        } else if (this.partsOrderUpdate && !this.cartData.bikes) {
          this.orderNumber = this.partsOrderUpdate;
          this.createThankYouMessage(this.partsOrderUpdate);
        } else if (!this.bikesOrderUpdate && this.cartData.bikes) {
          this.orderNumber = this.bikesOrderUpdate;
          this.emptyReturnError = true;
        } else if (!this.partsOrderUpdate && this.cartData.accessories && !this.cartData.bikes) {
          this.orderNumber = this.partsOrderUpdate;
          this.emptyReturnError = true;
        }
      } else {
        if (this.tabSelected === 'parts') {
          if (!this.partsOrderUpdate) {
            this.orderNumber = this.partsOrderUpdate;
            this.emptyReturnError = true;
          } else {
            this.emptyReturnError = false;
            this.orderNumber = this.partsOrderUpdate;
            this.createThankYouMessage(this.partsOrderUpdate);
          }
        }
        if (this.tabSelected === 'bikes') {
          if (!this.bikesOrderUpdate) {
            this.orderNumber = this.bikesOrderUpdate;
            this.emptyReturnError = true;
          } else {
            this.emptyReturnError = false;
            this.orderNumber = this.bikesOrderUpdate;
            this.createThankYouMessage(this.bikesOrderUpdate);
          }
        }
      }
    },

    getOrderNumber() {
      if (!this.orderData) {
        axios
          .get(`/checkout/multi/order-review/jdeOrderNumber?orderCode=${this.orderCode}`)
          .then(({data}) => {
            if (data.data.bikesOrderCode || data.data.partsOrderCode) {
              this.orderData = data.data;
              if (!this.bikesOrderUpdate && this.orderData.bikesOrderCode) {
                this.bikesOrderUpdate = this.orderData.bikesOrderCode;
              }
              if (!this.partsOrderUpdate && this.orderData.partsOrderCode) {
                this.partsOrderUpdate = this.orderData.partsOrderCode;
              }
              this.setMessages(true);
            }
          })
          .catch((error) => {
            console.log(error);
          })
          .finally(() => (this.loading = false));
      }
    },

    asyncOrderCheck() {
      const asyncPingInterval = 3000;
      const asyncPingTimeout = 60000;
      this.getOrderNumber();
      this.asyncOrderInterval = setInterval(this.getOrderNumber, asyncPingInterval);
      setTimeout(() => {
        clearInterval(this.asyncOrderInterval);
        if (!this.orderData) this.timeoutError = true;
      }, asyncPingTimeout);
    },

    createThankYouMessage(order) {
      this.thankYouMessage = this.orderConfirmationCallout[0].label;
      let msg = this.thankYouMessage.replace('{0}', order);
      this.thankYouMessage = msg;
    },

    displayUnavailableItemsWarnings() {
      if (!isEmpty(this.unavailableItems)) {
        this.unavailableItems.forEach((item) => {
          setTimeout(() => {
            return this.$notify({
              type: 'warning',
              message: this.skuNotAvailableText.replace('{0}', item),
            });
          }, 1000);
        });
      }
    },

    updateShippingDate(payload) {
      if (
        payload.partType === 'bikes' &&
        this.cartData.bikes.orderDetails &&
        this.cartData.bikes.orderDetails.shipmentInfo
      ) {
        this.cartData.bikes.orderDetails.shipmentInfo.deliveryDate = payload.value;
      } else if (
        payload.partType === 'parts' &&
        this.cartData.parts.orderDetails &&
        this.cartData.parts.orderDetails.shipmentInfo
      ) {
        this.cartData.parts.orderDetails.shipmentInfo.deliveryDate = payload.value;
      }
    },

    changeGridTab(tab) {
      this.tabSelected = tab;

      $('html, body').animate(
        {
          scrollTop: $(this.$refs.checkoutTabs.$el).offset().top - 150,
        },
        500
      );
    },

    // Show shipment warning if there is more than 1 warehouse
    showShipmentWarning(type) {
      if (this.checkoutStep === 'orderReview' || this.checkoutStep === 'confirmation') return false;

      if (type === 'bikes') {
        return get(this, 'cartData.bikes.warehouses', []).length > 1;
      } else if (type === 'parts') {
        return get(this, 'cartData.parts.warehouses', []).length > 1;
      } else {
        return false;
      }
    },

    // Handles the data update to the BE when the user navigates away from Shipments page
    handleShipmentsBeforeUnload(successURL) {
      axios
        .put('/checkout-b2b/shipments', this.compileData())
        .then((response) => {
          let responseError = get(response, 'data.meta.feedback.message.basePropertyKey', null);
          if (responseError === 'checkout.B2B.unableSaveSelections') {
            this.showSaveWarning = true;
          } else {
            window.location = successURL;
          }
        })
        .catch((error) => console.log(error));
    },

    // Handles the data update to the BE when the user navigates away from Shipments page
    performCartValidation() {
      axios
        .post('/checkout-b2b/validateCart', this.compileData())
        .then((response) => {
          let responseError = get(response, 'data.meta.feedback.message.basePropertyKey', null);
          if (responseError === 'checkout.B2B.cartValidationFailure') {
            this.showCartValidationWarning = true;
            $('.place-order-button').prop('disabled', true);
          }
        })
        .catch((error) => console.log(error));
    },

    compileData() {
      let compiledData = {};

      let bikeCompiledData = this.getCompiledPartType(this.cartData.bikes);
      let partsCompiledData = this.getCompiledPartType(this.cartData.parts);

      if (bikeCompiledData) compiledData.bikes = bikeCompiledData;
      if (partsCompiledData) compiledData.parts = partsCompiledData;

      return compiledData;
    },

    getCompiledPartType(partData) {
      if (isEmpty(partData) || isEmpty(partData.orderDetails)) return null;

      let deliveryDate = get(partData, 'orderDetails.shipmentInfo.deliveryDate', null);

      if (!deliveryDate) {
        deliveryDate = this.getTodaysDate();
      }
      return {
        customerPO: partData.orderDetails.customerPO,
        portofDelivery: partData.orderDetails.portofDelivery,
        holdNote: partData.orderDetails.holdNote,
        holdValue: partData.orderDetails.holdValue,
        holdCode: partData.orderDetails.holdCode,
        selectedShipping: partData.orderDetails.selectedShipping,
        deliveryDate: this.getISODeliveryDate(deliveryDate),
        warehouses: this.getCompiledWarehouseData(partData.warehouses),
      };
    },

    // this method will return current date in yyyy-MM-dd format.
    getTodaysDate() {
      var currentDate = new Date();
      var yyyy = currentDate.getFullYear().toString();
      var mm = (currentDate.getMonth() + 1).toString(); // getMonth() is zero-based
      var dd = currentDate.getDate().toString();

      return yyyy + '-' + (mm[1] ? mm : '0' + mm[0]) + '-' + (dd[1] ? dd : '0' + dd[0]);
    },

    getCompiledWarehouseData(warehouses) {
      if (isEmpty(warehouses)) return [];

      return map(warehouses, (warehouse) => {
        return {
          id: warehouse.id,
          isBackOrdered: this.getBackOrderStatus(warehouse),
          items: map(warehouse.entries, (item) => {
            return {
              sku: item.sku,
              qty: warehouse.id === 'backOrdered' ? item.backorderedQty : item.nonBackorderedQty,
              isAllocated: item.isAllocated,
            };
          }),
        };
      });
    },

    getFreightWarehouseData(warehouses) {
      if (isEmpty(warehouses)) return [];

      return map(warehouses, (warehouse) => {
        return {
          id: warehouse.id,
          isBackOrdered: this.getBackOrderStatus(warehouse),
        };
      });
    },

    getBackOrderStatus({id, isPrimary, isBackOrdered}) {
      if (isPrimary) {
        return false;
      } else if (id === 'backOrdered') {
        return true;
      } else {
        return isBackOrdered;
      }
    },

    getISODeliveryDate(date) {
      if (!date) return null;

      let deliveryDate = new Date(date);

      return deliveryDate.toISOString();
    },

    enableShipmentsSubmitListeners() {
      $('.js-shipments-listener').click(
        $.proxy(function(event) {
          event.preventDefault();
          this.$validator.validateAll().then((result) => {
            if (!result) {
              const firstErrorFieldName = Object.keys(this.$validator.errors.collect())[0];
              if (firstErrorFieldName.startsWith('parts')) {
                this.changeGridTab('parts');
              } else if (firstErrorFieldName.startsWith('bikes')) {
                this.changeGridTab('bikes');
              }
              return;
            }
            this.handleShipmentsBeforeUnload(event.currentTarget.href);
          });
        }, this)
      );
    },

    getFreightObject() {
      let data = {bikes: {}, parts: {}};

      data['isAllocatedItemsPage'] = false;
      if (this.cartData.bikes && this.cartData.bikes.orderDetails) {
        data.bikes['warehouses'] = this.getCompiledWarehouseData(this.cartData.bikes.warehouses);
        data.bikes['selectedShipping'] = this.cartData.bikes.orderDetails.selectedShipping;
      }

      if (this.cartData.parts && this.cartData.parts.orderDetails) {
        data.parts['warehouses'] = this.getCompiledWarehouseData(this.cartData.parts.warehouses);
        data.parts['selectedShipping'] = this.cartData.parts.orderDetails.selectedShipping;
      }
      return data;
    },

    recalculateFreight(cartType) {
      axios
        .put('/cart/freight', this.getFreightObject())
        .then(() => {
          this.loadCart().finally(() => {
            this.calculatedFreight[cartType] = true;
          });
        })
        .catch((error) => console.log(error));
    },

    loadCart() {
      return axios
        .get(this.endpoint)
        .then(({data}) => {
          this.cartData = data.data;
          this.validateCartData();
        })
        .catch((error) => {
          console.log(error);
        })
        .finally(() => (this.loading = false));
    },
  },
};
</script>
